// Component classification system
// Types: The functional grouping (Buttons, Tiles, Input, etc.)
// Categories: The architectural level (Blocks, Components, Containers, Chrome)

// Available types
export const componentTypes = [
  'Buttons',
  'Tiles',
  'Input',
  'Images',
  'Navigation',
  'Frames',
  'Overlays',
  'Sections',
  'Misc',
] as const

export type ComponentType = (typeof componentTypes)[number]

// Available categories
export const componentCategories = [
  'Blocks',
  'Components',
  'Containers',
  'Chrome',
] as const

export type ComponentCategory = (typeof componentCategories)[number]

// Component definition with types and categories
export interface ComponentDefinition {
  id: string
  label: string
  types: ComponentType[]
  categories: ComponentCategory[]
}

// All component definitions
export const componentDefinitions: ComponentDefinition[] = [
  // Buttons type
  { id: 'button-category', label: 'ButtonCategory', types: ['Buttons'], categories: ['Blocks'] },
  { id: 'button-search', label: 'ButtonSearch', types: ['Buttons'], categories: ['Blocks'] },
  { id: 'button-small', label: 'ButtonSmall', types: ['Buttons'], categories: ['Blocks', 'Components'] },
  { id: 'button-large', label: 'ButtonLarge', types: ['Buttons'], categories: ['Blocks', 'Components'] },
  { id: 'button-medium', label: 'ButtonMedium', types: ['Buttons'], categories: ['Blocks', 'Components'] },
  { id: 'button-checkout', label: 'ButtonCheckout', types: ['Buttons'], categories: ['Components'] },
  { id: 'button-location', label: 'ButtonLocation', types: ['Buttons'], categories: ['Components'] },
  { id: 'button-order', label: 'ButtonOrder', types: ['Buttons'], categories: ['Components'] },
  { id: 'button-icon-large', label: 'ButtonIconLarge', types: ['Buttons'], categories: ['Components'] },
  { id: 'button-icon-small', label: 'ButtonIconSmall', types: ['Buttons'], categories: ['Blocks'] },
  { id: 'button-favorite', label: 'ButtonFavorite', types: ['Buttons'], categories: ['Components'] },
  { id: 'button-quick-favorites', label: 'ButtonQuickFavorites', types: ['Buttons'], categories: ['Components'] },
  { id: 'button-quick-reorder', label: 'ButtonQuickReorder', types: ['Buttons'], categories: ['Components'] },
  { id: 'button-add-product', label: 'ButtonAddProduct', types: ['Buttons'], categories: ['Components'] },
  { id: 'button-tip', label: 'ButtonTip', types: ['Buttons'], categories: ['Components'] },
  { id: 'button-notification', label: 'ButtonNotification', types: ['Buttons'], categories: ['Components'] },
  { id: 'button-product', label: 'ButtonProduct', types: ['Buttons'], categories: ['Components'] },
  { id: 'button-product-reorder', label: 'ButtonProductReorder', types: ['Buttons'], categories: ['Components'] },
  { id: 'tab', label: 'Tab', types: ['Buttons'], categories: ['Blocks'] },
  { id: 'custom-tip-option', label: 'CustomTipOption', types: ['Buttons'], categories: ['Components'] },
  { id: 'tip-presets', label: 'TipPresets', types: ['Buttons'], categories: ['Containers'] },
  { id: 'notification-types', label: 'NotificationTypes', types: ['Buttons'], categories: ['Containers'] },

  // Tiles type
  { id: 'tile-page', label: 'TilePage', types: ['Tiles'], categories: ['Components'] },
  { id: 'tile-product', label: 'TileProduct', types: ['Tiles'], categories: ['Components'] },
  { id: 'tile-offer', label: 'TileOffer', types: ['Tiles'], categories: ['Components'] },
  { id: 'tile-order-history', label: 'TileOrderHistory', types: ['Tiles'], categories: ['Components'] },
  { id: 'tile-rewards', label: 'TileRewards', types: ['Tiles'], categories: ['Components'] },
  { id: 'badge', label: 'Badge', types: ['Misc'], categories: ['Blocks'] },

  // Input type
  { id: 'text-field', label: 'TextField', types: ['Input'], categories: ['Blocks', 'Components'] },
  { id: 'text-field-search', label: 'TextFieldSearch', types: ['Input'], categories: ['Components'] },
  { id: 'address-search-result', label: 'AddressSearchResult', types: ['Input'], categories: ['Blocks'] },
  { id: 'radio-button', label: 'RadioButton', types: ['Input'], categories: ['Blocks'] },
  { id: 'checkbox', label: 'Checkbox', types: ['Input'], categories: ['Blocks'] },
  { id: 'text-field-label', label: 'TextFieldLabel', types: ['Input'], categories: ['Blocks'] },
  { id: 'toggle-order-type', label: 'ToggleOrderType', types: ['Input'], categories: ['Components'] },
  { id: 'product-count', label: 'ProductCount', types: ['Input'], categories: ['Components'] },
  { id: 'text-block', label: 'TextBlock', types: ['Input'], categories: ['Components'] },
  { id: 'text-field-credit-card', label: 'TextFieldCreditCard', types: ['Input'], categories: ['Components'] },
  { id: 'option-payment-choice', label: 'OptionPaymentChoice', types: ['Input'], categories: ['Blocks'] },
  { id: 'option-row', label: 'OptionRow', types: ['Input'], categories: ['Components'] },
  { id: 'field-group-card-information', label: 'FieldGroupCardInformation', types: ['Input'], categories: ['Containers'] },
  { id: 'field-group-address-details', label: 'FieldGroupAddressDetails', types: ['Input'], categories: ['Containers'] },
  { id: 'field-group-account-information', label: 'FieldGroupAccountInformation', types: ['Input'], categories: ['Containers'] },
  { id: 'delivery-preference', label: 'DeliveryPreference', types: ['Input'], categories: ['Containers'] },

  // Containers category (type matches their main contained component)
  { id: 'menu-category', label: 'MenuCategory', types: ['Tiles'], categories: ['Containers'] },
  { id: 'tabs-container', label: 'TabsContainer', types: ['Buttons'], categories: ['Containers'] },
  { id: 'account-page-navigation', label: 'AccountPageNavigation', types: ['Tiles'], categories: ['Containers'] },
  { id: 'help-page-navigation', label: 'HelpPageNavigation', types: ['Tiles'], categories: ['Containers'] },
  { id: 'quick-buttons-container', label: 'QuickButtonsContainer', types: ['Buttons'], categories: ['Containers'] },
  { id: 'product-choices', label: 'ProductChoices', types: ['Buttons'], categories: ['Containers'] },
  { id: 'offer-group', label: 'OfferGroup', types: ['Tiles'], categories: ['Containers'] },
  { id: 'order-history', label: 'SectionOrderHistory', types: ['Sections'], categories: ['Containers'] },
  { id: 'order-history-sheet', label: 'OrderHistorySheet', types: ['Tiles'], categories: ['Containers'] },
  { id: 'reward-points-group', label: 'RewardPointsGroup', types: ['Tiles'], categories: ['Containers'] },
  { id: 'reorder-products-list', label: 'ReorderProductsList', types: ['Buttons'], categories: ['Containers'] },
  { id: 'favorites-list', label: 'SectionFavoritesList', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-reorder', label: 'SectionReorder', types: ['Sections'], categories: ['Containers'] },
  { id: 'location-list', label: 'LocationList', types: ['Buttons'], categories: ['Containers'] },

  // Images type
  { id: 'image-product-button', label: 'ImageProductButton', types: ['Images'], categories: ['Blocks'] },
  { id: 'image-product-tile', label: 'ImageProductTile', types: ['Images'], categories: ['Blocks'] },
  { id: 'image-reward', label: 'ImageReward', types: ['Images'], categories: ['Blocks'] },
  { id: 'image-product-sheet', label: 'ImageProductSheet', types: ['Images'], categories: ['Blocks'] },
  { id: 'image-offer', label: 'ImageOffer', types: ['Images'], categories: ['Blocks'] },
  { id: 'image-banner', label: 'ImageBanner', types: ['Images'], categories: ['Blocks'] },
  { id: 'image-brand-logo', label: 'ImageBrandLogo', types: ['Images'], categories: ['Blocks'] },

  // Navigation type
  { id: 'navigation-desktop', label: 'NavigationDesktop', types: ['Navigation'], categories: ['Chrome'] },
  { id: 'navigation-mobile', label: 'NavigationMobile', types: ['Navigation'], categories: ['Chrome'] },

  // Frames type (headers and footers)
  { id: 'toolbar', label: 'Toolbar', types: ['Frames'], categories: ['Chrome'] },
  { id: 'header-menu', label: 'HeaderMenu', types: ['Frames'], categories: ['Chrome'] },
  { id: 'header-cart', label: 'HeaderCart', types: ['Frames'], categories: ['Chrome'] },
  { id: 'header-rewards', label: 'HeaderRewards', types: ['Frames'], categories: ['Chrome'] },
  { id: 'header-other', label: 'HeaderOther', types: ['Frames'], categories: ['Chrome'] },
  { id: 'location-order-type', label: 'LocationOrderType', types: ['Frames'], categories: ['Chrome'] },
  { id: 'footer-desktop', label: 'FooterDesktop', types: ['Frames'], categories: ['Chrome'] },

  // Overlays type
  { id: 'alert', label: 'Alert', types: ['Overlays'], categories: ['Chrome'] },
  { id: 'alert-add-card', label: 'AlertAddCard', types: ['Overlays'], categories: ['Chrome'] },
  { id: 'sheet', label: 'Sheet', types: ['Overlays'], categories: ['Chrome'] },
  { id: 'sheet-grabber', label: 'SheetGrabber', types: ['Overlays'], categories: ['Components'] },
  { id: 'sheet-close-button', label: 'SheetCloseButton', types: ['Overlays'], categories: ['Components'] },

  // Sections type
  { id: 'section-add-to-order', label: 'SectionAddToOrder', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-address-results', label: 'SectionAddressResults', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-app-issue-form', label: 'SectionAppIssueForm', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-checkout-driver-tip', label: 'SectionCheckoutDriverTip', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-checkout-payment-method', label: 'SectionCheckoutPaymentMethod', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-feedback-form', label: 'SectionFeedbackForm', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-notification-type', label: 'SectionNotificationType', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-offer-details', label: 'SectionOfferDetails', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-option-group', label: 'SectionOptionGroup', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-order-issue-form', label: 'SectionOrderIssueForm', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-order-summary', label: 'SectionOrderSummary', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-product-information', label: 'SectionProductInformation', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-products-in-cart', label: 'SectionProductsInCart', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-required-product', label: 'SectionRequiredProduct', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-special-requests', label: 'SectionSpecialRequests', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-previous-order-summary', label: 'SectionPreviousOrderSummary', types: ['Sections'], categories: ['Containers'] },
  { id: 'section-subtotal', label: 'SectionSubtotal', types: ['Sections'], categories: ['Containers'] },

  // Misc type
  { id: 'divider', label: 'Divider', types: ['Misc'], categories: ['Blocks'] },
  { id: 'toast', label: 'Toast', types: ['Misc'], categories: ['Blocks'] },
  { id: 'discount-banner', label: 'DiscountBanner', types: ['Misc'], categories: ['Components'] },
  { id: 'discount-banner-container', label: 'DiscountBannerContainer', types: ['Misc'], categories: ['Containers'] },
  { id: 'required-indicator', label: 'RequiredIndicator', types: ['Misc'], categories: ['Blocks'] },
  { id: 'order-status', label: 'OrderStatus', types: ['Misc'], categories: ['Components'] },
  { id: 'map-icon', label: 'MapIcon', types: ['Misc'], categories: ['Blocks'] },
  { id: 'order-status-icon', label: 'OrderStatusIcon', types: ['Misc'], categories: ['Blocks'] },
  { id: 'order-status-progress-bar', label: 'OrderStatusProgressBar', types: ['Misc'], categories: ['Blocks'] },
  { id: 'text-order-status', label: 'TextOrderStatus', types: ['Misc'], categories: ['Blocks'] },
  { id: 'caret', label: 'Caret', types: ['Misc'], categories: ['Blocks'] },
  { id: 'gauge-circular', label: 'GaugeCircular', types: ['Misc'], categories: ['Blocks'] },
  { id: 'gauge-linear', label: 'GaugeLinear', types: ['Misc'], categories: ['Blocks'] },
  { id: 'order-line-item', label: 'OrderLineItem', types: ['Misc'], categories: ['Components'] },
  { id: 'cart-item', label: 'CartItem', types: ['Misc'], categories: ['Components'] },
  { id: 'previous-cart-item', label: 'PreviousCartItem', types: ['Misc'], categories: ['Components'] },
  { id: 'selected-order', label: 'SelectedOrder', types: ['Misc'], categories: ['Components'] },
  { id: 'points-number-only', label: 'PointsNumberOnly', types: ['Misc'], categories: ['Containers'] },
]

// Helper: Get components grouped by type (for display)
// Each component appears only once, in its primary (first) type group
// Components with multiple types will show all their types in the card UI
export function getComponentsByType(): { type: ComponentType; components: ComponentDefinition[] }[] {
  const typeOrder = componentTypes
  const grouped = new Map<ComponentType, ComponentDefinition[]>()

  // Initialize with empty arrays in order
  typeOrder.forEach(type => grouped.set(type, []))

  // Group components - each component only appears in its primary (first) type group
  componentDefinitions.forEach(comp => {
    const primaryType = comp.types[0]
    if (primaryType) {
      const existing = grouped.get(primaryType) || []
      existing.push(comp)
      grouped.set(primaryType, existing)
    }
  })

  // Convert to array, filtering out empty types
  return typeOrder
    .map(type => ({ type, components: grouped.get(type) || [] }))
    .filter(group => group.components.length > 0)
}

// Helper: Get all unique types that have components
export function getAvailableTypes(): ComponentType[] {
  const types = new Set<ComponentType>()
  componentDefinitions.forEach(comp => {
    comp.types.forEach(type => types.add(type))
  })
  return componentTypes.filter(type => types.has(type))
}

// Helper: Get all unique categories that have components
export function getAvailableCategories(): ComponentCategory[] {
  const categories = new Set<ComponentCategory>()
  componentDefinitions.forEach(comp => {
    comp.categories.forEach(cat => categories.add(cat))
  })
  return componentCategories.filter(cat => categories.has(cat))
}

// Helper: Filter components by type and/or categories
export function filterComponents(
  typeFilters: Set<ComponentType>,
  categoryFilters: Set<ComponentCategory>
): ComponentDefinition[] {
  return componentDefinitions.filter(comp => {
    // Check type filter (component must have at least one matching type)
    if (typeFilters.size > 0) {
      const hasMatchingType = comp.types.some(type => typeFilters.has(type))
      if (!hasMatchingType) {
        return false
      }
    }

    // Check category filter (component must have at least one matching category)
    if (categoryFilters.size > 0) {
      const hasMatchingCategory = comp.categories.some(cat => categoryFilters.has(cat))
      if (!hasMatchingCategory) {
        return false
      }
    }

    return true
  })
}

// Helper: Get component by ID
export function getComponentById(id: string): ComponentDefinition | undefined {
  return componentDefinitions.find(comp => comp.id === id)
}

// Helper: Get ordered components list (for navigation in preview)
export function getOrderedComponentIds(): string[] {
  return componentDefinitions.map(comp => comp.id)
}
